import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { MultipleChoiceForm } from '@/components/ui/multiple-choice-form';
import { shuffleQuestions, type QuizQuestion } from '@/lib/quiz-parser';
import { remapQuestionForDisplay } from '@/lib/quiz-option-shuffle';
import { submitQuizPracticeSession, type QuizSelection } from '@/lib/api/quiz';
import { testIds } from '@/lib/test-ids';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  formatQuizSessionScope,
  QuizSessionHeader,
} from './QuizSessionHeader';

interface QuizPracticeModeProps {
  questions: QuizQuestion[];
  slug: string;
  shuffleOptions?: boolean;
  selection?: QuizSelection | null;
  onDone?: () => void;
  /** Exit to domain/mode picker. Parent applies confirm rules from answeredCount. */
  onExitSession?: (answeredCount: number) => void;
}

type PracticeCard = QuizQuestion & { optionOrder?: string[] };

/**
 * Untimed Practice tab: immediate MCQ feedback, no exam quiz_runs.
 */
export function QuizPracticeMode({
  questions,
  slug,
  shuffleOptions = true,
  selection = null,
  onDone,
  onExitSession,
}: QuizPracticeModeProps) {
  const scopeLabel = formatQuizSessionScope(selection);
  const [deck, setDeck] = useState<PracticeCard[]>(() => {
    const ordered = shuffleQuestions(questions);
    return ordered.map((q) =>
      shuffleOptions ? remapQuestionForDisplay(q) : { ...q },
    );
  });
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<
    Record<string, { choice: string; correct: boolean }>
  >({});
  const [revealed, setRevealed] = useState(false);
  const [finished, setFinished] = useState(false);
  const [saving, setSaving] = useState(false);
  const [startedAt] = useState(() => new Date());
  const [summary, setSummary] = useState<{
    correctCount: number;
    total: number;
    weakDomains: string[];
  } | null>(null);
  const answeredCount = Object.keys(answers).length;

  useEffect(() => {
    const ordered = shuffleQuestions(questions);
    setDeck(
      ordered.map((q) => (shuffleOptions ? remapQuestionForDisplay(q) : { ...q })),
    );
    setIndex(0);
    setAnswers({});
    setRevealed(false);
    setFinished(false);
    setSummary(null);
  }, [questions, shuffleOptions]);

  const reshuffleDeck = useCallback(() => {
    const ordered = shuffleQuestions(questions);
    setDeck(
      ordered.map((q) => (shuffleOptions ? remapQuestionForDisplay(q) : { ...q })),
    );
    setIndex(0);
    setAnswers({});
    setRevealed(false);
    setFinished(false);
    setSummary(null);
  }, [questions, shuffleOptions]);

  const current = deck[index];
  const options = useMemo(
    () =>
      Object.entries(current?.options || {}).map(([id, label]) => ({
        id,
        label,
      })),
    [current]
  );

  const finish = useCallback(
    async (finalAnswers: Record<string, { choice: string; correct: boolean }>) => {
      const correctCount = Object.values(finalAnswers).filter((a) => a.correct).length;
      const domainTotals: Record<string, number> = {};
      const domainCorrect: Record<string, number> = {};
      for (const q of deck) {
        const d = q.domain || 'general';
        domainTotals[d] = (domainTotals[d] || 0) + 1;
        if (finalAnswers[q.id]?.correct) {
          domainCorrect[d] = (domainCorrect[d] || 0) + 1;
        }
      }
      const weakDomains = Object.keys(domainTotals).filter((d) => {
        const pct = ((domainCorrect[d] || 0) / domainTotals[d]) * 100;
        return pct < 70;
      });
      setSummary({ correctCount, total: deck.length, weakDomains });
      setFinished(true);

      if (!slug) return;
      setSaving(true);
      try {
        await submitQuizPracticeSession(slug, {
          answers: finalAnswers,
          source: 'document_tab',
          started_at: startedAt.toISOString(),
          completed_at: new Date().toISOString(),
          selection,
        });
        toast.success('Practice session saved');
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Failed to save practice');
      } finally {
        setSaving(false);
      }
    },
    [deck, slug, startedAt, selection]
  );

  if (!current) {
    return (
      <p className="text-sm text-muted-foreground p-6">No questions to practice.</p>
    );
  }

  const sessionHeader = onExitSession ? (
    <QuizSessionHeader
      mode="practice"
      scopeLabel={scopeLabel}
      onExitSession={() => onExitSession(answeredCount)}
      disabled={saving}
    />
  ) : null;

  if (finished && summary) {
    return (
      <div
        className="flex flex-col h-full min-h-0"
        data-testid={testIds.quizDocumentRenderer.practiceMode}
      >
        {sessionHeader}
        <div className="p-6 space-y-4">
          <h3 className="text-lg font-semibold">
            Practice complete: {summary.correctCount}/{summary.total}
            {saving ? ' (saving...)' : ''}
          </h3>
          {summary.weakDomains.length > 0 ? (
            <p className="text-sm text-muted-foreground">
              Weak domains: {summary.weakDomains.join(', ')}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">No weak domains flagged.</p>
          )}
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={reshuffleDeck}>
              Practice again
            </Button>
            {onDone ? (
              <Button size="sm" onClick={onDone}>
                Take timed quiz
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col h-full min-h-0"
      data-testid={testIds.quizDocumentRenderer.practiceMode}
    >
      {sessionHeader}
      <div className="px-5 py-3 border-b border-border text-sm shrink-0 flex justify-between">
        <span className="tabular-nums font-medium">
          Practice {index + 1} / {deck.length}
        </span>
        <span className="text-muted-foreground">No timer - immediate feedback</span>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">
          {(current.domain || 'general').replace(/_/g, ' ')}
        </p>
        <MultipleChoiceForm
          question={current.stem}
          options={options}
          value={answers[current.id] ? [answers[current.id].choice] : []}
          revealed={
            revealed
              ? {
                  correctIds: [String(current.answer).toLowerCase()],
                  explanation: current.explanation,
                }
              : undefined
          }
          disabled={revealed}
          onOptionActivate={(id) => {
            const correct = id.toLowerCase() === String(current.answer).toLowerCase();
            setAnswers((prev) => ({
              ...prev,
              [current.id]: { choice: id, correct },
            }));
            setRevealed(true);
          }}
        />
      </div>
      <div className="px-5 py-3 border-t border-border shrink-0 flex justify-end">
        {revealed ? (
          <Button
            size="sm"
            onClick={() => {
              if (index < deck.length - 1) {
                setIndex((i) => i + 1);
                setRevealed(false);
              } else {
                void finish(answers);
              }
            }}
          >
            {index < deck.length - 1 ? (
              'Next'
            ) : saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Saving...
              </>
            ) : (
              'Finish practice'
            )}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
